
import React from "react";
import { i18nManager } from "../../i18n";
import Simulate from "../simulate/Simulate";
import { FormStep, Schemes, Utils } from "../../utils";
import { Svgs } from "../../constants/Images";
import { Status } from "../../constants/Status";
import { Button, Dropdown, NoseurObject } from "@rivtn/noseur";
import { PaymentMethod } from "../../constants/PaymentMethod";
import { ITransaction, ITransactionCharge, PloutousService, SanitizedResponse, WalletUssdBankProcessorOrMobileMoneyOperator } from "../../services";

export interface UssdProps {
    businessId: string;
    transaction: ITransaction;
    paymentSuccessful: () => void;
    loadingState: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
    fetchTransaction: (onDone?: (result?: ITransaction, error?: any) => void) => void;
}

function Ussd(props: UssdProps) {

    const transaction = props.transaction;
    const [_, setLoading] = props.loadingState;
    const labels = i18nManager.Labels.method.ussd;
    const [ussdCode, setUssdCode] = React.useState("");
    const ploutousService = PloutousService.getInstance();
    const [errorMessages, setErrorMessages] = React.useState<string[]>([]);
    const [serviceErrors, setServiceErrors] = React.useState<string[]>([]);
    const [chargeData, setChargeData] = React.useState<NoseurObject<any>>();
    const [serviceWarnings, setServiceWarnings] = React.useState<string[]>([]);
    const [serviceSuccesses, setServiceSuccesses] = React.useState<string[]>([]);
    const [confirmingPayment, setConfirmingPayment] = React.useState<boolean>(false);
    const [step, setStep] = React.useState<FormStep.SELECT_BANK | FormStep.CONFIRM_PAYMENT>(FormStep.SELECT_BANK);
    const countryAndCurrency = i18nManager.CountriesAndCurrencies.find(l => l.value === transaction.charge_country_code);
    const processors: WalletUssdBankProcessorOrMobileMoneyOperator[] = transaction.payment_options.find(p => p.method === PaymentMethod.USSD)?.ussd_bank_processors ?? [];
    const [selectedWalletUssdBankProcessorOrMobileMoneyOperator, setSelectedWalletUssdBankProcessorOrMobileMoneyOperator] = React.useState<WalletUssdBankProcessorOrMobileMoneyOperator>();

    if (serviceSuccesses.length) {
        return Utils.buildPaymentCompletedView(transaction, serviceSuccesses);
    }

    if (serviceWarnings.length) {
        return Utils.buildPartialPaymentView(transaction, serviceWarnings, () => {
            setServiceWarnings([]);
            setStep(FormStep.SELECT_BANK);
        }, () => {
            setServiceWarnings([]);
            setStep(FormStep.CONFIRM_PAYMENT);
        });
    }

    if (serviceErrors.length) {
        return Utils.buildPaymentErrorView(transaction, serviceErrors, () => {
            setConfirmingPayment(false);
            setStep(FormStep.SELECT_BANK);
            setServiceErrors([]);
        });
    }

    return (<div className="ussd credit-card">
        <span className="title">{labels.title}</span>
        <div className="card-info" style={{ marginTop: 30 }}>
            <span className="title">{labels.choose_bank}</span>
            <span className="desc">{labels.desc}</span>
            <Dropdown fill scheme={Schemes.RIVTN_PLOUTOUS} placeholder={labels.select_bank}
                disabled={step === FormStep.CONFIRM_PAYMENT} // TODO: should be readOnly, add in noseur
                options={processors} optionMap={{
                    value: "code",
                    label: "name",
                    icon: "logo_location",
                }} onSelectOption={(o: WalletUssdBankProcessorOrMobileMoneyOperator) => {
                    setSelectedWalletUssdBankProcessorOrMobileMoneyOperator(o);
                }} />
        </div>
        {step === FormStep.CONFIRM_PAYMENT && (<>
            <span className="i">{labels.simply_dial}</span>
            <div className="code">
                <span>{ussdCode}</span>
                {Svgs.Copy(copyUssdCode)} {/* tooltip from noseur */}
            </div>
            {(chargeData?.processor === "SIM") && (<Simulate currency={countryAndCurrency?.currency_sign} pages={{
                one: {
                    inputType: "PLAIN",
                    defaultInput: ussdCode,
                    content: labels.enter_ussd_code,
                },
                two: {
                    inputType: "AMOUNT",
                    defaultInput: `${(transaction.amount/ Utils.AMOUNT_UNIT)}`,
                    action: async (simulate: "SUCCESS" | "FAILURE", inputOne?: string, amount?: string) => {
                        try {
                            if (inputOne !== ussdCode) {
                                throw new Error("The ussd code is invalid");
                            }
                            const payload = {
                                simulate,
                                amount: parseFloat(amount ?? "-1"),
                            };
                            await ploutousService.simulateChargePayment(chargeData.charge_data, payload);
                            Utils.toastSuccess(i18nManager.Labels.simulator.successful);
                        } catch (error: any) {
                            PloutousService.reportError(error);
                        }
                    }
                }
            }} />)}
        </>)}
        {errorMessages.map((errorMessage) => <span key={errorMessage} className="err">{errorMessage}</span>)}
        <Button raised fill scheme={Schemes.RIVTN_PLOUTOUS} className="pay"
            text={step === FormStep.SELECT_BANK ? i18nManager.Labels.common.next : i18nManager.Labels.common.confirm_payment}
            {...(confirmingPayment ? Utils.BUTTON_LOADING_PROPS : {})} disabled={!((step === FormStep.SELECT_BANK && selectedWalletUssdBankProcessorOrMobileMoneyOperator) || (step === FormStep.CONFIRM_PAYMENT)) || confirmingPayment}
            onClick={() => proceedToPay()} />
        {step === FormStep.CONFIRM_PAYMENT && (<Button raised outlined fill scheme={Schemes.RIVTN_PLOUTOUS} className="pay"
            text={i18nManager.Labels.common.back} style={{ marginTop: 20 }} onClick={() => {
                setErrorMessages([]);
                setStep(FormStep.SELECT_BANK);
                setConfirmingPayment(false);
            }} />)}
    </div>);

    async function proceedToPay() {
        setLoading(true);
        setErrorMessages([]);
        if (step === FormStep.SELECT_BANK) {
            ploutousService.initiateCharge(transaction, props.businessId, PaymentMethod.USSD, { ussd_bank_code: selectedWalletUssdBankProcessorOrMobileMoneyOperator?.code }).then(({ sanitized }: SanitizedResponse<ITransactionCharge>) => {
                setUssdCode(sanitized.result.ussd_code);
                setChargeData({ ...sanitized.extra_data, processor: sanitized.processor });
                setStep(FormStep.CONFIRM_PAYMENT);
            }).catch((error: any) => {
                setServiceErrors(error.errors ?? [error.message]);
            }).finally(() => {
                setLoading(false);
            });
            return;
        }
        confirmPayment();
    }

    async function confirmPayment(showModal: boolean = true) {
        if (showModal) setLoading(true);
        props.fetchTransaction((result?: ITransaction, error?: any) => {
            setLoading(false);
            if (error) {
                setServiceErrors(error.errors ?? [error.message]);
                return;
            }
            if ((result?.status ?? 0) === Status.TRANSACTION_PARTIALLY_PAID) {
                setServiceWarnings([i18nManager.Labels.common.partially_paid]);
                return;
            } else if ((result?.status ?? 0) > Status.TRANSACTION_FULLY_PAID_PENDING_ACKNOWLEDGEMENT) {
                props.paymentSuccessful();
                setServiceSuccesses([i18nManager.Labels.common.thank_you, i18nManager.Labels.common.payment_successful]);
                return;
            }
            if (!confirmingPayment) setConfirmingPayment(true);
            setTimeout(() => confirmPayment(false), 2000);
            console.log("Initiate web socket connection to track payment completion");
            // TODO in the API service implements logic to fetch every 2 seconds 5 times from processor once a verify transaction is received
            // also for double webhook log it to be able to detect duplicate payments from processor
        });
    }

    async function copyUssdCode() {
        Utils.copyToClipboard(ussdCode, (err) => {
            if (!err) {
                //queryCopiedPopOverManageRef.current?.show(e);
                //setTimeout(() => queryCopiedPopOverManageRef.current?.hide(), 500);
            } else {
                console.error(err);
            }
        });
    }

}

export default Ussd;